import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import {
  FolderOpen,
  Upload,
  Trash2,
  Download,
  AlertTriangle,
  FileText,
} from 'lucide-react';
import {
  getAgentFiles,
  postAgentFiles,
  removeAgentFile,
  removeAgentFolder,
  getAgentFileText,
} from '../data';
import { downloadAgentFile } from '../api';
import SkillFileTree from '../components/SkillFileTree';
import './AgentPages.css';
import './AgentKnowledgeFiles.css';

function formatBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function languageForPath(path) {
  if (!path) return 'plaintext';
  const lower = path.toLowerCase();
  if (lower.endsWith('.md') || lower.endsWith('.markdown')) return 'markdown';
  if (lower.endsWith('.py')) return 'python';
  if (lower.endsWith('.json')) return 'json';
  if (lower.endsWith('.yaml') || lower.endsWith('.yml')) return 'yaml';
  if (lower.endsWith('.js') || lower.endsWith('.mjs') || lower.endsWith('.cjs')) return 'javascript';
  if (lower.endsWith('.ts')) return 'typescript';
  if (lower.endsWith('.tsx') || lower.endsWith('.jsx')) return 'javascript';
  if (lower.endsWith('.html') || lower.endsWith('.htm')) return 'html';
  if (lower.endsWith('.css')) return 'css';
  if (lower.endsWith('.sh') || lower.endsWith('.bash')) return 'shell';
  if (lower.endsWith('.sql')) return 'sql';
  if (lower.endsWith('.csv') || lower.endsWith('.tsv') || lower.endsWith('.txt') || lower.endsWith('.log')) return 'plaintext';
  if (lower.endsWith('.xml')) return 'xml';
  return 'plaintext';
}

function fileToPath(f) {
  return f.folder ? `${f.folder}/${f.filename}` : f.filename;
}

export default function AgentKnowledgeFiles() {
  const { agentId } = useOutletContext();
  const [files, setFiles] = useState([]);
  const [limits, setLimits] = useState({ max_file_bytes: 1024 * 1024, max_files_per_agent: 200 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [activePath, setActivePath] = useState(null);
  const [activeContent, setActiveContent] = useState('');
  const [activeLoading, setActiveLoading] = useState(false);
  const [activeIsText, setActiveIsText] = useState(true);

  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const data = await getAgentFiles(agentId);
      setFiles(data?.files || []);
      if (data?.limits) setLimits(data.limits);
    } catch (e) {
      setErrorMsg(e?.message || 'Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    if (agentId) load();
  }, [agentId, load]);

  const pathToFile = useMemo(() => {
    const m = {};
    for (const f of files) m[fileToPath(f)] = f;
    return m;
  }, [files]);

  const allPaths = useMemo(() => files.map(fileToPath).sort(), [files]);

  const activeFile = activePath ? pathToFile[activePath] : null;

  // Auto-pick first file when files load and nothing is selected
  useEffect(() => {
    if (!activePath && allPaths.length > 0) {
      setActivePath(allPaths[0]);
    }
    // If active path no longer exists, clear it
    if (activePath && !pathToFile[activePath]) {
      setActivePath(allPaths[0] || null);
      setActiveContent('');
    }
  }, [allPaths, activePath, pathToFile]);

  // Load file content when active file changes
  useEffect(() => {
    if (!activeFile) {
      setActiveContent('');
      setActiveIsText(true);
      return;
    }
    let cancelled = false;
    setActiveLoading(true);
    setActiveContent('');
    (async () => {
      try {
        const data = await getAgentFileText(agentId, activeFile.id);
        if (cancelled) return;
        setActiveIsText(!!data?.is_text);
        setActiveContent(data?.content || '');
      } catch (e) {
        if (cancelled) return;
        setActiveIsText(true);
        setActiveContent(`// Failed to load: ${e?.message || 'unknown error'}`);
      } finally {
        if (!cancelled) setActiveLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [agentId, activeFile?.id]);

  const upload = async (fileList, _opts = {}) => {
    if (!fileList || !fileList.length) return;
    setBusy(true);
    setErrorMsg('');
    try {
      const result = await postAgentFiles(agentId, Array.from(fileList));
      if (result?.errors?.length) {
        setErrorMsg(
          result.errors.map((e) => `${e.filename}: ${e.error}`).join(' • '),
        );
      }
      await load();
    } catch (e) {
      setErrorMsg(e?.message || 'Upload failed');
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (folderInputRef.current) folderInputRef.current.value = '';
    }
  };

  const onDeleteActive = async () => {
    if (!activeFile) return;
    if (!confirm(`Delete "${fileToPath(activeFile)}"?`)) return;
    try {
      await removeAgentFile(agentId, activeFile.id);
      setActivePath(null);
      await load();
    } catch (e) {
      setErrorMsg(e?.message || 'Delete failed');
    }
  };

  const onDownloadActive = async () => {
    if (!activeFile) return;
    try {
      await downloadAgentFile(agentId, activeFile.id, activeFile.filename);
    } catch (e) {
      setErrorMsg(e?.message || 'Download failed');
    }
  };

  const folders = useMemo(() => {
    const set = new Set();
    for (const f of files) if (f.folder) set.add(f.folder);
    return Array.from(set);
  }, [files]);

  const onDeleteFolder = async (folderName) => {
    if (!confirm(`Delete folder "${folderName}" and all its files?`)) return;
    try {
      await removeAgentFolder(agentId, folderName);
      await load();
    } catch (e) {
      setErrorMsg(e?.message || 'Delete failed');
    }
  };

  const totalBytes = files.reduce((s, f) => s + (f.size_bytes || 0), 0);

  return (
    <div className="kf-workspace">
      <div className="kf-ws-hero">
        <div className="kf-ws-hero-top">
          <div className="kf-ws-hero-icon"><FolderOpen size={20} /></div>
          <div className="kf-ws-hero-text">
            <h2>Knowledge Files</h2>
            <p>
              Upload company info as folders or individual files. Before every
              incoming event, a context-retrieval model picks the most relevant
              ones and feeds them to the agent automatically.
            </p>
            <div className="kf-ws-hero-meta">
              <span>{files.length} / {limits.max_files_per_agent} files</span>
              <span>•</span>
              <span>{formatBytes(totalBytes)} total</span>
              <span>•</span>
              <span>Max {formatBytes(limits.max_file_bytes)} per file</span>
            </div>
          </div>
        </div>
        <div className="kf-ws-actions">
          <button
            className="kf-btn kf-btn-primary"
            onClick={() => folderInputRef.current?.click()}
            disabled={busy}
          >
            <Upload size={14} /> Upload folder
          </button>
          <button
            className="kf-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
          >
            <Upload size={14} /> Upload files
          </button>
          {busy && <span className="kf-busy">Uploading…</span>}
          <input
            ref={folderInputRef}
            type="file"
            multiple
            // eslint-disable-next-line react/no-unknown-property
            webkitdirectory=""
            directory=""
            style={{ display: 'none' }}
            onChange={(e) => upload(e.target.files)}
          />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => upload(e.target.files)}
          />
        </div>
      </div>

      {errorMsg && (
        <div className="kf-error kf-ws-error">
          <AlertTriangle size={14} /> {errorMsg}
        </div>
      )}

      <div className="kf-editor">
        <div className="kf-editor-tree">
          {folders.length > 0 && (
            <div className="kf-tree-folder-bar">
              <span className="kf-tree-folder-bar-label">Folders</span>
              <div className="kf-tree-folder-chips">
                {folders.map((fld) => (
                  <button
                    key={fld}
                    type="button"
                    className="kf-tree-folder-chip"
                    title={`Delete folder ${fld}`}
                    onClick={() => onDeleteFolder(fld)}
                  >
                    {fld} <Trash2 size={11} />
                  </button>
                ))}
              </div>
            </div>
          )}
          {loading ? (
            <div className="kf-tree-empty">Loading…</div>
          ) : files.length === 0 ? (
            <div className="kf-tree-empty">
              No files uploaded yet. Use the buttons above to upload a folder
              of company docs (pricing, FAQs, policies, product specs…).
            </div>
          ) : (
            <SkillFileTree
              files={allPaths}
              activePath={activePath}
              onPick={setActivePath}
            />
          )}
        </div>

        <div className="kf-editor-pane">
          <div className="kf-editor-bar">
            <div className="kf-editor-path">
              {activeFile ? (
                <>
                  <FileText size={12} style={{ marginRight: 6, opacity: 0.7 }} />
                  {fileToPath(activeFile)}
                  <span className="kf-editor-meta">
                    {formatBytes(activeFile.size_bytes)}
                    {!activeFile.is_text && <span className="kf-binary-tag">binary</span>}
                  </span>
                </>
              ) : (
                <span style={{ opacity: 0.6 }}>— no file selected —</span>
              )}
            </div>
            <div className="kf-editor-actions">
              {activeFile && (
                <>
                  <button
                    type="button"
                    className="kf-btn kf-btn-ghost"
                    onClick={onDownloadActive}
                    title="Download"
                  >
                    <Download size={13} />
                  </button>
                  <button
                    type="button"
                    className="kf-btn kf-btn-danger"
                    onClick={onDeleteActive}
                    title="Delete file"
                  >
                    <Trash2 size={13} />
                  </button>
                </>
              )}
            </div>
          </div>
          {!activeFile ? (
            <div className="kf-editor-empty">
              {files.length === 0
                ? 'Upload some files to get started.'
                : 'Select a file from the tree to preview its content.'}
            </div>
          ) : !activeIsText ? (
            <div className="kf-editor-empty">
              <div style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: 6, color: 'var(--text-secondary)' }}>
                  Binary file ({activeFile.mime_type || 'unknown'})
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Preview is only available for text files. Use Download to retrieve it.
                </div>
              </div>
            </div>
          ) : activeLoading ? (
            <div className="kf-editor-empty">Loading…</div>
          ) : (
            <div className="kf-editor-monaco">
              <Editor
                height="100%"
                theme="vs-dark"
                language={languageForPath(activeFile.filename)}
                value={activeContent}
                options={{
                  readOnly: true,
                  minimap: { enabled: false },
                  fontSize: 13,
                  fontFamily: "'SF Mono', 'Monaco', 'Inter', monospace",
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                  wordWrap: 'on',
                  tabSize: 2,
                  renderLineHighlight: 'gutter',
                }}
              />
            </div>
          )}
        </div>
      </div>

      {files.length === 0 && (
        <div className="kf-tip">
          <h4>Tip: name your folders descriptively</h4>
          <div className="kf-tip-tags">
            <span className="kf-tip-tag">pricing/</span>
            <span className="kf-tip-tag">policies/</span>
            <span className="kf-tip-tag">faqs/</span>
            <span className="kf-tip-tag">product-specs/</span>
            <span className="kf-tip-tag">team-contacts/</span>
          </div>
          <p className="kf-hint">
            The retriever uses folder names as a primary signal — a folder
            called <code>pricing/</code> is much more likely to be picked when
            an incoming message mentions cost.
          </p>
        </div>
      )}
    </div>
  );
}
